/** @file syncQuadOverride: mirrors a quad drag into the store; drops a typed quad once the preview drew it. */
import type { QuadOverride } from '../../state/editorTypes';
import type { QuadEdit, ViewportCtx } from './viewportCtx';

const same = (a: readonly number[], b: readonly number[]): boolean => a[0] === b[0] && a[1] === b[1];

/** Whether the stored override already says what drag `e` shows. */
function mirrors(o: QuadOverride | null, e: QuadEdit): boolean {
  return o !== null && o.by === 'drag' && o.drawable === e.drawable && o.entry === e.ref.entry && same(o.size, e.size) && same(o.pivot, e.pivot);
}

/** A typed override whose batch landed and whose rebuilt preview is the one drawn (or whose rebuild failed). */
function typedDrawn(ctx: ViewportCtx, o: QuadOverride, drawn: unknown): boolean {
  const p = ctx.store.getState().preview;
  if (o.until === null) return false;
  return p.error !== null || (p.revision >= o.until && !p.loading && drawn === p.bundle);
}

/**
 * Once per frame: a running (or held) handle drag is written to `quadOverride` as `by: 'drag'` (only when its values
 * changed), and cleared once the drag is gone; a `by: 'typed'` override is cleared once the preview draws its batch.
 * `drawn` is the bundle the viewport draws now.
 */
export function syncQuadOverride(ctx: ViewportCtx, drawn: unknown): void {
  const s = ctx.store.getState();
  const o = s.quadOverride;
  const e = ctx.quad.edit;
  if (e !== null && e.moved) {
    if (!mirrors(o, e)) {
      ctx.store.setState({ quadOverride: { drawable: e.drawable, entry: e.ref.entry, size0: e.size0, pivot0: e.pivot0, size: e.size, pivot: e.pivot, by: 'drag', until: null } });
    }
    return;
  }
  if (o?.by === 'drag' || (o?.by === 'typed' && typedDrawn(ctx, o, drawn))) ctx.store.setState({ quadOverride: null });
}
